import { Injectable } from '@angular/core';

import { BetState } from '@bpos/v1/my-bets';
import { OfferSource } from '@cds';
import { MyBetsOption } from '@cds/my-bets';
import { ScoreboardSlim } from '@cds/scoreboards/v1';
import { PlayerStats } from '@cds/statistics/player';
import { EventParticipant, MarketTypes, OptionTypes, Tv2MarketParameters } from '@frontend/sports/betting-offer/feature/model';
import { PeriodType } from '@frontend/sports/betting-offer/feature/types';
import { EpcotConfig, MyBetsVisualizationTrackingConfig } from '@frontend/sports/common/client-config-data-access';
import { toNumber } from 'lodash-es';

import { BetBuilderLegState, BetStateCssClass, MyBetsBet, MyBetsBetBuilderLeg } from '../../my-bets-base/models/my-bets-viewmodels';
import { MyBetsEventModel } from '../models/my-bets-event.model';
import {
    BetMarketInfo,
    MarketStatInfo,
    VisualizationBetState,
    VisualizationConstants,
    VisualizationTracking,
    VisualizationType,
    betStateMapping,
} from '../models/my-bets-visualization-types';

export interface Stats {
    player1Stat: number;
    player2Stat: number;
}

@Injectable()
export class MyBetsVisualizationService {
    private isEpcotEnabled: boolean;

    constructor(
        private mybetsVisualizationTrackingConfig: MyBetsVisualizationTrackingConfig,
        private epcotConfig: EpcotConfig,
    ) {
        this.isEpcotEnabled = this.epcotConfig.isEnabled;
    }

    getVisualizationDetails(event: MyBetsEventModel | undefined, sportId: number, marketId?: number): VisualizationTracking {
        if (!event || !this.isTrackingEnabled(event, sportId)) {
            return this.noVisualization();
        }

        const marketStatInfo = event.marketStatInfo?.find((x) => x.marketId === marketId);
        if (!marketStatInfo?.marketStats?.length) {
            return this.noVisualization(true);
        }

        const marketStat = this.marketStatsDetails(event, marketId);
        const flattenedStats = this.getFlattenedPlayerStats(event.playerStats || []);

        const hasPlayerId =
            marketStatInfo.options.some((x) => x.playerId && flattenedStats.hasOwnProperty(x.playerId)) ||
            marketStatInfo.playerIds.some((id) => flattenedStats.hasOwnProperty(id));

        const isTeamTypeWithoutScoreboard = marketStat.statsType === VisualizationConstants.Team && !event.fixtureScoreboard;
        const isPlayerTypeWithoutStats = marketStat.statsType !== VisualizationConstants.Team && !(event.playerStats?.length && hasPlayerId);

        if (isTeamTypeWithoutScoreboard || isPlayerTypeWithoutStats) {
            return this.noVisualization(true);
        }

        return {
            isTrackingEnabled: true,
            visualizationType: this.getVisualizationType(marketStatInfo, sportId),
        };
    }

    isTrackingEnabled(event: MyBetsEventModel | undefined, sportId: number): boolean {
        if (!event?.league) {
            return false;
        }

        const { isEnabled, allowedLeagues } = this.mybetsVisualizationTrackingConfig;
        const leagueId = event.league.parentLeagueId || event.league.id;

        return isEnabled && !!leagueId && (allowedLeagues[sportId]?.length === 0 || allowedLeagues[sportId]?.includes(leagueId));
    }

    private noVisualization(isEnabled = false): VisualizationTracking {
        return {
            isTrackingEnabled: isEnabled,
            visualizationType: VisualizationType.NoVisualization,
        };
    }

    getBetMarketInfoFromBet(bet: MyBetsBet): BetMarketInfo | null {
        const { marketId, marketName, optionName, optionId, betState } = {
            marketId: bet.marketId,
            marketName: bet.marketName,
            optionName: bet.optionName,
            optionId: bet.resultInfo?.optionId ?? bet.optionMarketInfo?.optionId,
            betState: bet.state,
        };

        return this.getMarketInfoIfValid(marketId, marketName, optionName, optionId, betState);
    }

    getBetMarketInfoFromLeg(myBetsBetBuilderLeg: MyBetsBetBuilderLeg): BetMarketInfo | null {
        const { marketId, marketName, optionName, optionId, betState } = {
            marketId: myBetsBetBuilderLeg.marketId,
            marketName: myBetsBetBuilderLeg.marketName,
            optionName: myBetsBetBuilderLeg.optionName,
            optionId: myBetsBetBuilderLeg.optionId,
            betState: myBetsBetBuilderLeg.betState,
        };

        return this.getMarketInfoIfValid(marketId, marketName, optionName, optionId, betState);
    }

    getBetStateClassName(
        betState: BetState | BetBuilderLegState,
        currStatValue: number,
        isCashedOut?: boolean,
        isSettled?: boolean,
        isNeitherMarket?: boolean,
        playerId?: number,
    ): string {
        if (isCashedOut) {
            return BetStateCssClass.BetInProgress;
        }

        const visualizationState = betStateMapping[betState];

        switch (visualizationState) {
            case VisualizationBetState.Open:
                if (currStatValue !== 0) {
                    return BetStateCssClass.BetInProgress;
                }
                break;
            case VisualizationBetState.Won:
                return this.getBetStateCssClass(BetStateCssClass.BetWon, isNeitherMarket, playerId, isSettled);
            case VisualizationBetState.Lost:
                return this.getBetStateCssClass(BetStateCssClass.BetLost, isNeitherMarket, playerId, isSettled);
            case VisualizationBetState.Cancelled:
                return BetStateCssClass.BetCancelled;
            default:
                return '';
        }

        return '';
    }

    getTargetStatByTemplateId(templateId: number): number[] | undefined {
        if (!templateId) {
            return undefined;
        }
        const config = this.mybetsVisualizationTrackingConfig.targetStatForMarket;
        for (const key in config) {
            if (config[key].includes(templateId)) {
                return [toNumber(key)];
            }
        }

        return undefined;
    }

    getTargetStatByName(name: string): number[] | undefined {
        const lineRegex = /[-+]?\d+([.,]\d+)?/g;
        const result = name.match(lineRegex);

        return result?.map((match) => toNumber(match.trim().replace(',', '.')));
    }

    getParticipants(eventParticipants: EventParticipant[]): { participant1: string; participant2: string } {
        if (eventParticipants.length < 2) {
            return { participant1: '', participant2: '' };
        }

        const [homeParticipant, awayParticipant] = eventParticipants;

        return {
            participant1: this.getParticipantName(homeParticipant),
            participant2: this.getParticipantName(awayParticipant),
        };
    }

    getCurrentStatValue(
        event: MyBetsEventModel | undefined,
        statsType: string,
        marketStatInfo?: MarketStatInfo,
        betMarketInfo?: BetMarketInfo,
    ): { player1Stat: number; player2Stat: number } {
        if (!event || !marketStatInfo) {
            return { player1Stat: 0, player2Stat: 0 };
        }
        const betOptionId = betMarketInfo?.optionId ?? 0;
        if (statsType === VisualizationConstants.Team && event.fixtureScoreboard) {
            return this.getTeamStatsValue(event.fixtureScoreboard, marketStatInfo);
        }

        if (statsType !== VisualizationConstants.Team && event.playerStats) {
            return this.getPlayerStatsValue(event.playerStats, marketStatInfo, betOptionId);
        }

        return { player1Stat: 0, player2Stat: 0 };
    }

    /**
     * Retrieves detailed statistics for a given market within an event.
     *
     * @param event - The event containing market-stats, mapped from my-bets fixture list (/my-bets-offer/fixtures/batch) mybetsVisualizationTracking is Enabled.
     * @param marketId - The ID of the market to retrieve stats for.
     * @returns An object containing the market statistics info, stats type, and a flag indicating if the market is in the current period.
     */
    marketStatsDetails(
        event?: MyBetsEventModel,
        marketId?: number,
    ): {
        marketStatsInfo: MarketStatInfo | undefined;
        statsType: string;
        marketInCurrentPeriod: boolean;
    } {
        const emptyMarketStatsDetails = { marketStatsInfo: undefined, statsType: '', marketInCurrentPeriod: false };

        if (!event || !event.marketStatInfo) {
            return emptyMarketStatsDetails;
        }

        const marketStatsInfo = event.marketStatInfo.find((x) => x.marketId === marketId);
        if (!marketStatsInfo) {
            return emptyMarketStatsDetails;
        }

        const statsType = marketStatsInfo.marketStats[0]?.split('.')?.[0] || '';

        return {
            marketStatsInfo,
            statsType,
            marketInCurrentPeriod: this.marketInCurrentPeriod(event, marketStatsInfo),
        };
    }

    private marketInCurrentPeriod = (event: MyBetsEventModel, marketStatsInfo: MarketStatInfo): boolean => {
        if (!event.scoreboard || !event.scoreboard.period || !marketStatsInfo.periodIdsMap) {
            return false;
        }

        const currentPeriodId = event.scoreboard.period.id;
        const marketPeriodValue = marketStatsInfo.parameters.find((o) => o.key === Tv2MarketParameters.Period)?.value;
        const marketPeriodId = marketPeriodValue && marketStatsInfo.periodIdsMap?.[marketPeriodValue];

        return !!(marketPeriodId && currentPeriodId && +marketPeriodId === currentPeriodId) || marketPeriodValue === PeriodType.RegularTime;
    };

    getLiveBetStatus(bet: MyBetsBet, event: MyBetsEventModel, myBetsBetBuilderLeg?: MyBetsBetBuilderLeg): VisualizationBetState {
        const betMarketInfo = myBetsBetBuilderLeg ? this.getBetMarketInfoFromLeg(myBetsBetBuilderLeg) : this.getBetMarketInfoFromBet(bet);

        if (!betMarketInfo) {
            return VisualizationBetState.Open;
        }
        const { marketStatsInfo, statsType, marketInCurrentPeriod } = this.marketStatsDetails(event, betMarketInfo.marketId);

        if (marketStatsInfo && statsType === VisualizationConstants.Team && marketInCurrentPeriod) {
            const currentStatValue = this.getTeamStatsValue(event.fixtureScoreboard, marketStatsInfo);
            const selectedOption = this.getOption(bet, marketStatsInfo, myBetsBetBuilderLeg);
            if (selectedOption) {
                switch (marketStatsInfo.marketType) {
                    case MarketTypes.ThreeWay:
                    case MarketTypes.TwoWay:
                        return this.betStateForMatchResultMarket(selectedOption, currentStatValue, event);
                    case MarketTypes.BTTS:
                        return this.betStateForBTTSMarket(selectedOption, currentStatValue);
                    case MarketTypes.OverUnderMarket:
                        return this.betStateForOverUnderMarket(selectedOption, bet, currentStatValue, myBetsBetBuilderLeg);
                    default:
                        return VisualizationBetState.Open;
                }
            }
        }
        return VisualizationBetState.Open;
    }

    private getBetStateCssClass(
        betStateClass: BetStateCssClass,
        isNeitherMarket?: boolean,
        playerId?: number,
        isSettled?: boolean,
    ): BetStateCssClass {
        if (isNeitherMarket && !playerId) {
            return BetStateCssClass.BetInProgress;
        }

        return isSettled ? BetStateCssClass.BetSettled : betStateClass;
    }

    private getFlattenedPlayerStats(playerStatsArray: PlayerStats[]): Record<number, Record<string, number>> {
        const flattenedStats: Record<number, Record<string, number>> = {};

        playerStatsArray.forEach((playerStats) => {
            Object.entries(playerStats.stats).forEach(([playerId, playerStat]) => {
                const id = Number(playerId);
                if (!flattenedStats[id]) {
                    flattenedStats[id] = playerStat;
                }
            });
        });

        return flattenedStats;
    }

    private getPlayerStatsValue(playerStatsArray: PlayerStats[], marketStatInfo: MarketStatInfo, betOptionId?: number): Stats {
        const flattenedStats = this.getFlattenedPlayerStats(playerStatsArray);

        const player1Id = marketStatInfo.options.find((o) => o.id === betOptionId)?.playerId;
        const playerIds = player1Id ? [player1Id] : marketStatInfo.playerIds;

        const computeStatData = (playerId: number): number => {
            const playerStat = flattenedStats[playerId];
            return playerStat && marketStatInfo.marketStats?.length
                ? marketStatInfo.marketStats.reduce((sum, stat) => sum + (playerStat[stat.toLowerCase()] ?? 0), 0)
                : 0;
        };

        const player1StatData = playerIds.reduce((sum, id) => sum + computeStatData(id), 0);

        const player2StatData =
            marketStatInfo.options.length === 2 && player1Id
                ? computeStatData(marketStatInfo.options.find((i) => i.playerId !== player1Id)?.playerId ?? 0)
                : 0;

        return { player1Stat: player1StatData, player2Stat: player2StatData };
    }

    private getTeamStatsValue(scoreboardTeamStats: ScoreboardSlim, marketStatInfo: MarketStatInfo): Stats {
        const result: Stats = { player1Stat: 0, player2Stat: 0 };

        if (!scoreboardTeamStats || !marketStatInfo.marketStats?.length) {
            return result;
        }

        marketStatInfo.marketStats.forEach((marketStat) => {
            const [marketName, player, period] = marketStat.replace(`${VisualizationConstants.Team}.`, '').split('.');
            const statValue = parseInt((scoreboardTeamStats[marketName as keyof ScoreboardSlim] as any)?.[player]?.[period]) || 0;

            if (player === VisualizationConstants.Player1) {
                result.player1Stat += statValue;
            } else if (player === VisualizationConstants.Player2) {
                result.player2Stat += statValue;
            }
        });

        return result;
    }

    private getVisualizationType(marketStatInfo: MarketStatInfo, sportId: number): VisualizationType {
        const { marketTemplateMapping, marketTypeMapping } = this.mybetsVisualizationTrackingConfig;
        const { source, templateId, marketType } = marketStatInfo;

        const { mappings, marketIdentifier } =
            source === OfferSource.V1
                ? { mappings: marketTemplateMapping?.[sportId], marketIdentifier: templateId }
                : { mappings: marketTypeMapping?.[sportId], marketIdentifier: marketType };

        if (!mappings) {
            return VisualizationType.NoVisualization;
        }

        const visualizationType = Object.keys(mappings).find((mapType) =>
            (mappings[mapType] as (number | string | undefined)[]).includes(marketIdentifier),
        );

        return (visualizationType as VisualizationType) || VisualizationType.NoVisualization;
    }

    private getParticipantName(eventParticipant: EventParticipant): string {
        return this.isEpcotEnabled ? eventParticipant.shortName || eventParticipant.name || '' : eventParticipant.name;
    }

    private getMarketInfoIfValid(
        marketId: number | undefined,
        marketName: string | undefined,
        optionName: string | undefined,
        optionId: number | undefined,
        betState: BetState | BetBuilderLegState,
    ): BetMarketInfo | null {
        if (!marketId || !marketName || !optionId || !optionName) {
            return null;
        }

        return {
            marketId,
            marketName,
            optionId,
            optionName,
            state: betState,
        };
    }

    private getOption(bet: MyBetsBet, marketStatsInfo: MarketStatInfo, myBetsBetBuilderLeg?: MyBetsBetBuilderLeg): MyBetsOption | undefined {
        const optionId = myBetsBetBuilderLeg?.optionId || bet.resultInfo?.optionId || bet.optionMarketInfo?.optionId;

        return marketStatsInfo.options.find((x) => x.id === optionId);
    }

    private betStateForMatchResultMarket(option: MyBetsOption, currentStatValue: Stats, event: MyBetsEventModel): VisualizationBetState {
        const optionType = option?.optionTypes[0];
        switch (optionType) {
            case OptionTypes.Draw:
                return currentStatValue.player1Stat === currentStatValue.player2Stat ? VisualizationBetState.Winning : VisualizationBetState.Losing;
            case OptionTypes.Max:
                if (event.participants[0].id === option.playerId) {
                    return currentStatValue.player1Stat > currentStatValue.player2Stat ? VisualizationBetState.Winning : VisualizationBetState.Losing;
                }

                if (event.participants[1].id === option.playerId) {
                    return currentStatValue.player2Stat > currentStatValue.player1Stat ? VisualizationBetState.Winning : VisualizationBetState.Losing;
                }

                return VisualizationBetState.Open;
            default:
                return VisualizationBetState.Open;
        }
    }

    private betStateForBTTSMarket(option: MyBetsOption, currentStatValue: Stats): VisualizationBetState {
        const optionType = option?.optionTypes[0];

        switch (optionType) {
            case OptionTypes.ToHappen:
                return currentStatValue.player1Stat > 0 && currentStatValue.player2Stat > 0
                    ? VisualizationBetState.Winning
                    : VisualizationBetState.Losing;
            case OptionTypes.NotToHappen:
                return currentStatValue.player1Stat + currentStatValue.player2Stat === 0
                    ? VisualizationBetState.Winning
                    : VisualizationBetState.Losing;

            default:
                return VisualizationBetState.Open;
        }
    }

    private betStateForOverUnderMarket(
        option: MyBetsOption,
        bet: MyBetsBet,
        currentStatValue: Stats,
        myBetsBetBuilderLeg?: MyBetsBetBuilderLeg,
    ): VisualizationBetState {
        const optionName = myBetsBetBuilderLeg?.optionName || bet?.optionName;
        const marketName = myBetsBetBuilderLeg?.marketName || bet?.marketName;
        const targetStatsValue = optionName ? this.getTargetStatByName(optionName) : this.getTargetStatByName(marketName);
        if (targetStatsValue) {
            const optionType = option?.optionTypes[0];
            switch (optionType) {
                case OptionTypes.Over:
                    return currentStatValue.player1Stat + currentStatValue.player2Stat > targetStatsValue[0]
                        ? VisualizationBetState.Winning
                        : VisualizationBetState.Losing;
                case OptionTypes.Under:
                    return currentStatValue.player1Stat + currentStatValue.player2Stat < targetStatsValue[0]
                        ? VisualizationBetState.Winning
                        : VisualizationBetState.Losing;

                default:
                    return VisualizationBetState.Open;
            }
        }
        return VisualizationBetState.Open;
    }
}
